import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import type { Location } from "@/types/weather";

interface FavoriteButtonProps {
  locationId: number;
  locationName: string;
  size?: "sm" | "md" | "lg";
}

export default function FavoriteButton({ locationId, locationName, size = "md" }: FavoriteButtonProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  // Share the same authenticated list as Saved and the global spot lists.
  // A failed status lookup must never be interpreted as "not saved".
  const favoritesQuery = useQuery<Location[]>({
    queryKey: ["/api/favorites"],
    queryFn: async () => {
      const response = await apiRequest("/api/favorites", { method: "GET" });
      return response.json();
    },
    enabled: isAuthenticated,
  });

  const isFavorite = isAuthenticated && !!favoritesQuery.data?.some(spot => spot.id === locationId);
  const mutationKey = ["favorite-toggle", locationId];
  const isUpdating = useIsMutating({ mutationKey }) > 0;

  const toggleFavoriteMutation = useMutation({
    mutationKey,
    mutationFn: async (wasSaved: boolean) => {
      try {
        await apiRequest(wasSaved ? `/api/favorites/${locationId}` : "/api/favorites", {
          method: wasSaved ? "DELETE" : "POST",
          ...(wasSaved ? {} : { body: { locationId } }),
        });
        return { wasSaved, alreadyChanged: false };
      } catch (error) {
        // Another tab can change the favorite after this list was loaded.
        // Reconcile that harmless conflict instead of exposing raw API JSON.
        const message = error instanceof Error ? error.message : "";
        if ((!wasSaved && message.startsWith("409:") && message.includes("Location already in favorites")) ||
            (wasSaved && message.startsWith("404:") && message.includes("Favorite not found"))) {
          return { wasSaved, alreadyChanged: true };
        }
        throw error;
      }
    },
    onSuccess: async ({ wasSaved, alreadyChanged }) => {
      // Keep every representation of this spot disabled until the shared
      // list has refreshed, so clicks cannot use its pre-mutation state.
      await queryClient.invalidateQueries({ queryKey: ["/api/favorites"] });
      toast({
        title: alreadyChanged
          ? (wasSaved ? "Already removed" : "Already saved")
          : (wasSaved ? "Removed from saved spots" : "Added to saved spots"),
        description: wasSaved
          ? `${locationName} is no longer in your saved spots.`
          : `${locationName} is in your saved spots.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Couldn't update saved spots",
        description: error.message.startsWith("401:")
          ? "Your session has expired. Please sign in again and try once more."
          : "Please try again in a moment. If this continues, contact support.",
        variant: "destructive",
      });
      // A lost response can still have committed the change on the server.
      void queryClient.invalidateQueries({ queryKey: ["/api/favorites"] });
    },
  });

  const handleToggleFavorite = () => {
    if (!isAuthenticated) {
      toast({
        title: "Sign in required",
        description: "Please sign in to save surf spots to your favorites.",
        variant: "destructive",
      });
      return;
    }

    if (!favoritesQuery.data) {
      toast({
        title: "Couldn't load saved spots",
        description: "Please check your connection or sign in again. We'll retry loading your saved spots.",
        variant: "destructive",
      });
      void favoritesQuery.refetch();
      return;
    }

    if (!isUpdating) toggleFavoriteMutation.mutate(isFavorite);
  };

  const sizeClasses = {
    sm: "h-6 w-6",
    md: "h-8 w-8",
    lg: "h-10 w-10",
  };

  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className={`${sizeClasses[size]} ${
        isFavorite 
          ? "bg-red-50 border-red-200 hover:bg-red-100 dark:bg-red-900/20 dark:border-red-800 dark:hover:bg-red-900/30" 
          : "bg-white border-gray-200 hover:bg-gray-50 dark:bg-emerald-900/10 dark:border-emerald-800 dark:hover:bg-emerald-900/20"
      }`}
      onClick={handleToggleFavorite}
      disabled={isUpdating || (isAuthenticated && favoritesQuery.isPending)}
      aria-pressed={isAuthenticated && favoritesQuery.data ? isFavorite : undefined}
      aria-label={!favoritesQuery.data && favoritesQuery.isError
        ? `Retry loading saved status for ${locationName}`
        : `${isFavorite ? "Remove" : "Save"} ${locationName}${isFavorite ? " from saved spots" : ""}`}
      title={isFavorite ? "Remove from saved spots" : "Save this spot"}
    >
      <Heart
        className={`${iconSizes[size]} ${
          isFavorite ? "fill-red-500 text-red-500 dark:fill-red-400 dark:text-red-400" : "text-gray-400 dark:text-emerald-400 hover:text-red-400 dark:hover:text-red-300"
        }`}
      />
    </Button>
  );
}